/* The building ladder: what a level costs, and what a level is worth.

   The mirror of `simulator/domain/ladder.py`, and it exists for the reason rule 1 states — a
   number on screen is a claim about what the server will do, so the price on a level-up button
   and the output on a building modal have to be the arithmetic the server charges and produces.

   Requirement 34. Both halves used to be linear (`UPGRADE_A * level + UPGRADE_B` against
   `X_PER_LEVEL * level`), so the gap between two presses never widened: one level always cost
   about one round of what that building already earned, at level 3 and at level 40 alike.

   **The price doubles.** `price(1)` is the first level's old price and `price(2)` the second;
   from there each level costs twice the last. Starting the doubling at `A + B` rather than at `B`
   is what makes "the first two levels cost what they cost today" true for every building and not
   only the ones where `A === B`.

   **The output grows by √2**, so a new level 1 is the old level 2 and every two levels double.
   That is forced rather than chosen: output is proportional to the square root of everything
   spent, which is the diminishing return the whole economy is balanced against, and keeping it
   means the price ratio must be the output ratio squared.

   `scale` alternates x1.5 and x1.333 rather than a flat x1.414 — the same √2 over any two levels —
   so every scale is an integer and an output stays as round a number as it was.

   The growth factor and the ceiling's headroom come from `GET /game-config/` under `ladder`, like
   every other balance constant; the two defaults here are only what to draw before it arrives. */

export const LADDER_GROWTH = 2
export const CAP_HEADROOM = 4

/* What a level is worth, in the levels this game had before requirement 34:
   `0, 2, 3, 4, 6, 8, 12, 16, 24, 32, ...` — a new level 1 is the old level 2, and every two
   levels double. Every output constant keeps the value it has and is multiplied by this instead
   of by the level, so no balance number is written twice. */
export const scale = (level) => {
  const n = Math.trunc(level || 0)
  if (n <= 0) return 0
  const half = Math.floor((n - 1) / 2)
  return ((n - 1) % 2 === 0 ? 2 : 3) * 2 ** half
}

/* What it costs to raise a building from `currentLevel` to the next one: `b`, then `a + b`, then
   doubling — so the first two levels are priced exactly as `a * level + b` priced them. */
export const upgradeCost = (currentLevel, a, b) => {
  const n = Math.trunc(currentLevel || 0)
  if (n <= 0) return b
  return (a + b) * LADDER_GROWTH ** (n - 1)
}

/* How many workers a human city may hold: `CAP_HEADROOM` times its next level's price.
   Derived from the price rather than a constant of its own, because a ceiling that cannot hold
   the next level is the freeze that once pinned every human building at level 1. */
export const populationCeiling = (currentLevel, a, b, headroom = CAP_HEADROOM) =>
  headroom * upgradeCost(currentLevel, a, b)
